import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera, X, Check, Trash2, Clock, Image as ImageIcon, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LocaleMemories = ({ localeId, isOwner, user }) => {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Add memory form
  const [imageUrl, setImageUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // For image upload
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchMemories();
  }, [localeId]);

  const fetchMemories = async () => {
    try {
      const status = isOwner ? 'all' : 'approved';
      const response = await axios.get(
        `${API_URL}/locale/${localeId}/memories?status=${status}`,
        { withCredentials: true }
      );
      setMemories(response.data.memories || []);
      setPendingCount(response.data.memories?.filter(m => m.status === 'pending').length || 0);
    } catch (error) {
      console.error('Failed to fetch memories');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage({ file, preview: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!selectedImage?.file) return null;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedImage.file);
      const response = await axios.post(
        `${API_URL}/posts/upload-image`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.url;
    } catch (error) {
      toast.error('Failed to upload image');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const submitMemory = async () => {
    let finalImageUrl = imageUrl;
    
    // Upload image if selected
    if (selectedImage) {
      finalImageUrl = await uploadImage();
      if (!finalImageUrl) return;
    }
    
    if (!finalImageUrl) {
      toast.error('Please add an image');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_URL}/locale/${localeId}/memories`,
        { image_url: finalImageUrl, caption, tags: [] },
        { withCredentials: true }
      );
      
      if (response.data.status === 'pending') {
        toast.success('Memory submitted! Waiting for approval.');
      } else {
        toast.success('Memory added!');
      }
      
      setShowAddForm(false);
      setImageUrl('');
      setCaption('');
      setSelectedImage(null);
      fetchMemories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add memory');
    } finally {
      setSubmitting(false);
    }
  };

  const approveMemory = async (memoryId) => {
    try {
      await axios.post(
        `${API_URL}/locale/${localeId}/memories/${memoryId}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success('Memory approved!');
      fetchMemories();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const rejectMemory = async (memoryId) => {
    try {
      await axios.post(
        `${API_URL}/locale/${localeId}/memories/${memoryId}/reject`,
        {},
        { withCredentials: true }
      );
      toast.success('Memory rejected');
      fetchMemories();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const deleteMemory = async (memoryId) => {
    try {
      await axios.delete(
        `${API_URL}/locale/${localeId}/memories/${memoryId}`,
        { withCredentials: true }
      );
      toast.success('Memory deleted');
      fetchMemories();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const approvedMemories = memories.filter(m => m.status === 'approved');
  const pendingMemories = memories.filter(m => m.status === 'pending');
  const displayMemories = showAll ? approvedMemories : approvedMemories.slice(0, 6);

  return (
    <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Camera className="w-5 h-5 text-primary" />
          Memories
          {approvedMemories.length > 0 && (
            <span className="text-sm font-normal text-gray-500">({approvedMemories.length})</span>
          )}
        </h3>
        <Button
          size="sm"
          onClick={() => setShowAddForm(!showAddForm)}
          className="rounded-full bg-primary/10 text-primary hover:bg-primary/20"
          data-testid="add-memory-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Memory
        </Button>
      </div>

      {/* Pending Memories for Business Owner */}
      {isOwner && pendingMemories.length > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
          <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4" />
            Pending Approval ({pendingMemories.length})
          </h4>
          <div className="grid grid-cols-3 gap-2">
            {pendingMemories.map(memory => (
              <div key={memory.memory_id} className="relative group">
                <img
                  src={memory.image_url}
                  alt=""
                  className="w-full aspect-square object-cover rounded-lg opacity-75"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                  <button
                    onClick={() => approveMemory(memory.memory_id)}
                    className="p-2 bg-green-500 rounded-full text-white hover:bg-green-600"
                    title="Approve"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => rejectMemory(memory.memory_id)}
                    className="p-2 bg-red-500 rounded-full text-white hover:bg-red-600"
                    title="Reject"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="absolute bottom-1 left-1 right-1">
                  <div className="flex items-center gap-1 bg-black/60 rounded px-1 py-0.5">
                    <Avatar className="w-4 h-4">
                      <AvatarImage src={memory.user?.avatar} />
                      <AvatarFallback className="text-[8px]">{memory.user?.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span className="text-[10px] text-white truncate">{memory.user?.name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Memory Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl overflow-hidden"
          >
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Share your experience! Your memory will be reviewed by the business before appearing.
            </p>
            
            {/* Image Upload */}
            <div className="mb-3">
              {selectedImage ? (
                <div className="relative inline-block">
                  <img src={selectedImage.preview} alt="Preview" className="max-h-32 rounded-lg" />
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl cursor-pointer hover:border-primary transition-colors">
                  <div className="text-center">
                    <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                    <span className="text-sm text-gray-500">Click to upload photo</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="sr-only"
                  />
                </label>
              )}
            </div>
            
            {/* Or URL */}
            <div className="mb-3">
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="Or paste image URL..."
                className="rounded-lg text-sm"
                disabled={!!selectedImage}
              />
            </div>
            
            {/* Caption */}
            <div className="mb-3">
              <Input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption (optional)"
                className="rounded-lg text-sm"
                maxLength={200}
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowAddForm(false); setSelectedImage(null); setImageUrl(''); setCaption(''); }}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={submitMemory}
                disabled={submitting || uploading || (!imageUrl && !selectedImage)}
                className="rounded-full bg-primary hover:bg-primary-700 text-white"
              >
                {submitting || uploading ? 'Submitting...' : 'Submit Memory'}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Approved Memories Grid */}
      {loading ? (
        <div className="grid grid-cols-3 gap-2">
          {[1,2,3].map(i => (
            <div key={i} className="aspect-square bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : approvedMemories.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Camera className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>No memories yet. Be the first to share!</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            {displayMemories.map(memory => (
              <div
                key={memory.memory_id}
                className="relative group aspect-square overflow-hidden rounded-lg"
              >
                <img
                  src={memory.image_url}
                  alt={memory.caption || ''}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-2 left-2 right-2">
                    <div className="flex items-center gap-1 mb-1">
                      <Avatar className="w-5 h-5 border border-white">
                        <AvatarImage src={memory.user?.avatar} />
                        <AvatarFallback className="text-[8px]">{memory.user?.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs text-white font-semibold truncate">{memory.user?.name}</span>
                    </div>
                    {memory.caption && (
                      <p className="text-xs text-white/90 line-clamp-2">{memory.caption}</p>
                    )}
                  </div>
                  {(memory.user_id === user?.user_id || isOwner) && (
                    <button
                      onClick={() => deleteMemory(memory.memory_id)}
                      className="absolute top-2 right-2 p-1.5 bg-red-500/80 rounded-full text-white hover:bg-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {approvedMemories.length > 6 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-3 py-2 text-sm text-primary font-semibold hover:bg-primary/5 rounded-lg flex items-center justify-center gap-1"
            >
              {showAll ? (
                <>Show Less <ChevronUp className="w-4 h-4" /></>
              ) : (
                <>Show All ({approvedMemories.length}) <ChevronDown className="w-4 h-4" /></>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default LocaleMemories;
